import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import Modal, { useConfirm } from '../../components/Modal';
import { useSocket } from '../../contexts/SocketContext';
import TabBar from '../../components/TabBar';
import api from '../../utils/api';
import { isNativeApp, isStandaloneDisplay, isGuideDismissed, dismissInstallGuide } from '../../utils/installState';
import { KeyRound, Clock, Wallet, User, Crown, Briefcase, ChevronRight, Trash2, AlertTriangle, Film, BarChart3, Download, Smartphone, CheckCircle, Wrench, Gauge, FileSpreadsheet } from 'lucide-react';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import { MaintenanceScreen } from '../../components/MaintenanceGate';
import IOSInstallGuide from '../../components/IOSInstallGuide';

export default function AdminMePage() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { subscribe } = useSocket() || {};

  // 系统维护模式（仅管理员可见/可操作）
  //   开关 + 自定义文案（更新内容 / 预计恢复时间等，最长 msgMax 字）。文案可以先写好保存，不开维护也行；
  //   管理员自己不受维护影响，看不到全屏页，所以提供“预览全屏”（用输入框里当前内容，不用先保存）。
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  const [msgSaved, setMsgSaved] = useState('');     // 服务器上已保存的文案
  const [msgDraft, setMsgDraft] = useState('');     // 输入框里的内容
  const [msgMax, setMsgMax] = useState(500);
  const [msgBusy, setMsgBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // 关闭维护时是否推送“系统已恢复”、以及随推送带上的“本次更新”说明（选填，最多 400 字）。
  // 这两项存在本浏览器里：刷新页面不会丢，取消勾选后刷新仍是取消（以前每次刷新都会变回默认勾选，容易误发）。
  const [notifyRecovery, setNotifyRecovery] = useState(() => { try { return localStorage.getItem('maint_notify_recovery') !== '0'; } catch { return true; } });
  const [updateNotes, setUpdateNotes] = useState(() => { try { return localStorage.getItem('maint_update_notes') || ''; } catch { return ''; } });
  useEffect(() => { try { localStorage.setItem('maint_notify_recovery', notifyRecovery ? '1' : '0'); } catch {} }, [notifyRecovery]);
  useEffect(() => { try { if (updateNotes) localStorage.setItem('maint_update_notes', updateNotes); else localStorage.removeItem('maint_update_notes'); } catch {} }, [updateNotes]);
  const NOTES_MAX = 400;
  const savedRef = useRef('');
  const applySaved = (m) => { savedRef.current = m; setMsgSaved(m); setMsgDraft(m); };
  const msgDirty = msgDraft.trim() !== msgSaved;
  const msgLen = [...msgDraft].length;

  // 只在进入页面时读一次（订阅函数如果每次渲染都变，也不能因此重新读取、把输入框里正在写的内容冲掉）
  React.useEffect(() => {
    if (!isAdmin) return;
    api.get('/admin/settings/maintenance').then(r => {
      setMaintenance(!!r.data.enabled);
      applySaved(r.data.message || '');
      if (r.data.maxLength) setMsgMax(r.data.maxLength);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  React.useEffect(() => {
    if (!isAdmin || !subscribe) return undefined;
    return subscribe((evt, payload) => {
      if (evt !== 'maintenance_updated') return;
      setMaintenance(!!payload?.enabled);   // 多个管理员同时在线时保持同步
      // 广播里的文案只在开启时才有；输入框里有没保存的修改时不覆盖
      if (payload?.enabled && typeof payload.message === 'string') {
        const prev = savedRef.current;
        savedRef.current = payload.message;
        setMsgSaved(payload.message);
        setMsgDraft(d => (d.trim() === prev ? payload.message : d));
      }
    });
  }, [isAdmin, subscribe]);

  const toggleMaintenance = async () => {
    if (maintenanceBusy) return;
    const next = !maintenance;
    const shownText = msgDraft.trim();
    const ok = await confirm(next ? '开启维护模式？' : '关闭维护模式？', {
      detail: next ? (
        <div>
          <div>开启后，除管理员外的所有人（含代理、普通用户、未登录访客）会立即看到全屏“系统升级中，请耐心等待…”，无法使用系统；已登录的用户不会被登出。</div>
          <div style={{ marginTop: 10, fontWeight: 600, color: 'var(--c-text)' }}>将显示的文案</div>
          <div style={{ marginTop: 4, padding: '8px 10px', borderRadius: 8, background: 'var(--c-bg)', whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto', overflowWrap: 'anywhere' }}>
            {shownText || '（没有填写，只显示默认提示）'}
          </div>
        </div>
      ) : (notifyRecovery
        ? (updateNotes.trim()
          ? (
            <div>
              <div>关闭后，所有用户将自动恢复访问，并收到“系统已恢复”通知，里面带上下面的更新说明；没上线的用户下次登录时也会看到。</div>
              <div style={{ marginTop: 10, fontWeight: 600, color: 'var(--c-text)' }}>将推送的更新说明</div>
              <div style={{ marginTop: 4, padding: '8px 10px', borderRadius: 8, background: 'var(--c-bg)', whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto', overflowWrap: 'anywhere' }}>
                {updateNotes.trim()}
              </div>
            </div>
          )
          : '关闭后，所有用户将自动恢复访问，并收到一条“系统已恢复”的通知（维护不足 1 分钟、或 10 分钟内已发过则不重复发）。')
        : '关闭后，所有用户将自动恢复访问，不会推送恢复通知。'),
      danger: next,
    });
    if (!ok) return;
    setMaintenanceBusy(true);
    try {
      // 输入框里有没保存的修改：开 / 关时一并保存，所见即所得
      const body = { enabled: next };
      if (msgDirty) body.message = msgDraft;
      if (!next) {
        body.notify = notifyRecovery;   // 关闭时是否推送“系统已恢复”
        if (notifyRecovery && updateNotes.trim()) body.notes = updateNotes;   // 带上更新说明
      }
      const r = await api.put('/admin/settings/maintenance', body);
      setMaintenance(!!r.data.enabled);
      applySaved(r.data.message || '');
      if (!next && r.data.recovery?.sent) setUpdateNotes('');   // 已推送出去，清空输入框，避免下次重复带上
      showToast(r.data.tip || (next ? '维护模式已开启' : '维护模式已关闭'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    } finally {
      setMaintenanceBusy(false);
    }
  };

  const saveMaintenanceMessage = async () => {
    if (msgBusy || !msgDirty) return;
    setMsgBusy(true);
    try {
      const r = await api.put('/admin/settings/maintenance', { message: msgDraft });
      applySaved(r.data.message || '');
      showToast(maintenance ? '文案已保存，正在显示的维护页已同步更新' : '文案已保存，开启维护后显示', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '保存失败', 'error');
    } finally {
      setMsgBusy(false);
    }
  };

  const [showNukeModal, setShowNukeModal] = useState(false);
  const [nukeInput, setNukeInput] = useState('');
  const [nuking, setNuking] = useState(false);
  // 清空预览：由服务端按“清空一切、只保留清单”实时算出，弹窗里看到的就是实际会发生的
  const [nukePreview, setNukePreview] = useState(null);
  const [nukePreviewErr, setNukePreviewErr] = useState('');
  const openNukeModal = async () => {
    setNukeInput(''); setNukePreview(null); setNukePreviewErr(''); setShowNukeModal(true);
    try {
      const r = await api.get('/data/nuke-preview');
      setNukePreview(r.data);
    } catch (err) {
      setNukePreviewErr(err.response?.data?.error || '读取清空范围失败');
    }
  };
  const fmtBytes = (n) => n >= 1073741824 ? (n / 1073741824).toFixed(1) + ' GB' : n >= 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB';

  // 提现审批
  const [wdPanel, setWdPanel]           = useState(false);
  const [wdList, setWdList]             = useState([]);
  const [wdPendingCount, setWdPendingCount] = useState(0);
  const [wdRejectId, setWdRejectId]     = useState(null);
  const [wdRejectReason, setWdRejectReason] = useState('');
  const [wdActionLoading, setWdActionLoading] = useState(false);


  const fetchWdPending = React.useCallback(async () => {
    try {
      const r = await api.get('/withdrawal/requests?status=pending');
      setWdPendingCount((r.data.list || []).length);
    } catch {}
  }, []);

  React.useEffect(() => { fetchWdPending(); }, [fetchWdPending]);

  // 从后台首页“提现审批”待办点进来：直接展开提现审批面板
  React.useEffect(() => {
    if (searchParams.get('open') === 'withdrawal') {
      openWdPanel();
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openWdPanel = async () => {
    setWdPanel(true);
    try {
      const r = await api.get('/withdrawal/requests?status=all');
      setWdList(r.data.list || []);
    } catch {}
  };

  const handleWdReview = async (request_id, action, reject_reason) => {
    setWdActionLoading(true);
    try {
      await api.post('/withdrawal/review', { request_id, action, reject_reason });
      showToast(action === 'approve' ? '已审批通过' : '已拒绝', 'success');
      setWdRejectId(null);
      setWdRejectReason('');
      fetchWdPending();
      const r = await api.get('/withdrawal/requests?status=all');
      setWdList(r.data.list || []);
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    } finally { setWdActionLoading(false); }
  };

  const NUKE_CONFIRM = '清空全部数据';

  const handleNuke = async () => {
    if (nukeInput !== NUKE_CONFIRM) {
      showToast('请输入完整确认文字', 'error');
      return;
    }
    setNuking(true);
    try {
      const res = await api.delete('/data/nuke-all');
      showToast(res.data.message, 'success');
      setShowNukeModal(false);
      setNukeInput('');
    } catch (err) {
      showToast(err.response?.data?.error || '清空失败', 'error');
    } finally {
      setNuking(false);
    }
  };

  // 我的：只放“不常用 / 设置类 / 只在这里有入口”的项。
  // 首页和底部导航已经有入口的（用户管理、结算、记录、图片管理、代提数据、号码溯源、二次审查、通知系统、获客素材）不再重复放。
  const menuItems = [
    // 「今日验证码」管理员可改可看；代理商只读查看入口（后端 GET 路由对代理开放，POST 仍是 adminOnly）
    { Icon: KeyRound,    label: '登录验证',   path: '/admin/verification' },
    ...(isAdmin ? [
      { Icon: Clock,       label: '工作时间', path: '/admin/work-hours' },
      { Icon: Gauge,       label: '配额管控', path: '/admin/rate-control' },
      { Icon: FileSpreadsheet, label: '定时汇总', path: '/admin/daily-summary' },
      { Icon: Film,        label: '教程设置',     path: '/admin/tutorial' },
    ] : []),
    { Icon: Wallet,        label: '提现审批',     action: 'withdrawal', badge: wdPendingCount },
    { Icon: BarChart3,     label: '派发详情',     path: '/admin/dispatch-details' },
    ...(isAdmin ? [
      { Icon: Trash2,      label: '清空系统数据', action: 'nuke', danger: true },
    ] : []),
  ];

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1 }}><User size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />我的</span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>
        <div className="card" style={{ textAlign: 'center', padding: 24 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--c-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            {isAdmin ? <Crown size={28} color="var(--c-primary)" /> : <Briefcase size={28} color="var(--c-primary)" />}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{user?.name || '管理员'}</div>
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginTop: 4 }}>
            {isAdmin ? '系统管理员' : '代理商'} · {user?.phone}
          </div>
        </div>

        {isAdmin && (
          <div className="card" style={{
            padding: '14px 16px',
            border: maintenance ? '1px solid var(--c-accent)' : '1px solid transparent',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Wrench size={18} color={maintenance ? 'var(--c-accent)' : 'var(--c-primary)'} strokeWidth={1.8} />
                  <span>系统维护模式</span>
                </div>
                <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5, color: maintenance ? 'var(--c-accent)' : 'var(--c-text-secondary)' }}>
                  {maintenance ? '已开启：除管理员外，所有人（含登录页访客）看到全屏“系统升级中”' : '已关闭：所有人正常访问'}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={maintenance}
                aria-label="系统维护模式"
                disabled={maintenanceBusy}
                onClick={toggleMaintenance}
                style={{
                  flexShrink: 0, width: 50, height: 30, borderRadius: 15, border: 'none', padding: 0,
                  position: 'relative', cursor: maintenanceBusy ? 'default' : 'pointer',
                  opacity: maintenanceBusy ? 0.6 : 1,
                  background: maintenance ? 'var(--c-accent)' : '#d1d1d6',
                  transition: 'background 0.2s',
                }}
              >
                <span style={{
                  position: 'absolute', top: 3, left: maintenance ? 23 : 3, width: 24, height: 24,
                  borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  transition: 'left 0.2s',
                }} />
              </button>
            </div>

            {/* 自定义文案 / 恢复通知设置：只在维护开启时展开，关闭维护时整块折叠隐藏。
                输入框里的内容存在上面的 state 里，折叠不会丢。 */}
            {maintenance && (
              <div style={{ marginTop: 14, borderTop: '0.5px solid var(--c-divider)', paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>自定义文案（选填）</span>
                  <span style={{ fontSize: 12, color: msgLen > msgMax ? 'var(--c-danger)' : 'var(--c-text-tertiary)' }}>{msgLen} / {msgMax}</span>
                </div>
                <textarea
                  className="input"
                  rows={5}
                  value={msgDraft}
                  onChange={e => setMsgDraft(e.target.value)}
                  placeholder={'例如：\n本次更新：\n1. 新增证券图片管理\n2. 修复已知问题\n预计 22:30 恢复'}
                  style={{ fontSize: 14, lineHeight: 1.6, resize: 'vertical', minHeight: 110 }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setPreviewOpen(true)}>
                    预览全屏
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" style={{ flex: 1 }}
                    disabled={!msgDirty || msgBusy || msgLen > msgMax} onClick={saveMaintenanceMessage}>
                    {msgBusy ? '保存中…' : (maintenance && msgDirty ? '保存并立即更新' : '保存文案')}
                  </button>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={notifyRecovery} onChange={e => setNotifyRecovery(e.target.checked)} />
                  <span>关闭维护时，向所有用户推送“系统已恢复”</span>
                </label>
                {notifyRecovery && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>更新说明（选填，随“系统已恢复”推送）</span>
                      <span style={{ fontSize: 12, color: [...updateNotes].length > NOTES_MAX ? 'var(--c-danger)' : 'var(--c-text-tertiary)' }}>{[...updateNotes].length} / {NOTES_MAX}</span>
                    </div>
                    <textarea
                      className="input"
                      rows={4}
                      value={updateNotes}
                      onChange={e => setUpdateNotes(e.target.value)}
                      placeholder={'例如：\n1. 每日汇总现在包含代理本人的单\n2. 证券图片管理可以重新编辑失败记录'}
                      style={{ fontSize: 14, lineHeight: 1.6, resize: 'vertical', minHeight: 90 }}
                    />
                    <div style={{ fontSize: 11, marginTop: 6, lineHeight: 1.6, color: 'var(--c-text-tertiary)' }}>
                      写了更新说明，就算维护不足 1 分钟或 10 分钟内刚推送过，也会照常推送。用户没在线时，下次登录会弹出来看。
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 11, marginTop: 8, lineHeight: 1.6, color: 'var(--c-text-tertiary)' }}>
                  改文案后，正在看维护页的人几秒内自动更新。管理员自己不受维护影响，想看效果点“预览全屏”。
                </div>
              </div>
            )}
          </div>
        )}
        {previewOpen && <MaintenanceScreen message={msgDraft} preview onClose={() => setPreviewOpen(false)} />}

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {menuItems.map((item, i) => (
            <div key={item.label} onClick={() => {
              if (item.action === 'nuke') { openNukeModal(); }
              else if (item.action === 'withdrawal') openWdPanel();
              else navigate(item.path);
            }}
              style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', borderBottom: i < menuItems.length - 1 ? '1px solid var(--c-border)' : 'none' }}>
              <span style={{ fontSize: 15, display: 'flex', alignItems: 'center', gap: 10 }}>
                <item.Icon size={18} color={item.danger ? 'var(--c-text-secondary)' : 'var(--c-primary)'} strokeWidth={1.8} />
                <span style={{ color: item.danger ? 'var(--c-text-secondary)' : undefined }}>{item.label}</span>
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {item.badge > 0 && (
                  <span style={{ background: 'var(--c-danger)', color: 'white', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 6px', minWidth: 16, textAlign: 'center' }}>
                    {item.badge}
                  </span>
                )}
                <ChevronRight size={16} color="var(--c-text-secondary)" />
              </div>
            </div>
          ))}
        </div>


        {/* App 安装入口 */}
        {(() => {
          const ua = navigator.userAgent || '';
          const isNative = isNativeApp();
          const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
          const isAndroid = /Android/.test(ua);
          const isStandalone = isStandaloneDisplay();
          if (isNative || isStandalone) return null;
          if (isIOS && isGuideDismissed()) return null;   // iOS Safari 里无法判断是否装过，用户点过“我已安装”就不再提示
          return (
            <div className="card" style={{ marginTop: 12, marginBottom: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Smartphone size={16} color="var(--c-primary)" />安装到手机
              </div>
              {isAndroid && (
                <a href="/app.apk" download="React.apk" style={{ textDecoration: 'none' }}>
                  <button className="btn btn-primary" style={{ width: '100%', height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Download size={18} />下载安卓安装包（APK）
                  </button>
                </a>
              )}
              {isIOS && (
                <div>
                  <IOSInstallGuide />
                  <div style={{ marginTop: 10 }}>
                    <button className="btn btn-outline" style={{ width: '100%', height: 38, fontSize: 13 }}
                      onClick={(e) => { dismissInstallGuide(); e.currentTarget.closest('.card').style.display = 'none'; }}>
                      我已安装，不再提示
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        <button className="btn btn-outline" onClick={() => { logout(); navigate('/login'); }} style={{ marginTop: 16, width: '100%', height: 46 }}>
          退出登录
        </button>
      </div>

      {/* ── 提现审批面板 ─────────────────────────────────────────────── */}
      {wdPanel && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-end', zIndex: 999,
        }} onClick={() => setWdPanel(false)}>
          <div style={{
            width: '100%', background: 'var(--c-surface)', borderRadius: '20px 20px 0 0',
            padding: '24px 20px 48px', maxHeight: '85vh', overflowY: 'auto',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 20 }}>提现审批</div>
            {wdList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 14, color: 'var(--c-text-tertiary)' }}>暂无记录</div>
            ) : wdList.map(item => {
              const statusMap = {
                pending:   { label: '待审批', color: 'var(--c-accent)' },
                approved:  { label: '审批通过', color: 'var(--c-primary)' },
                rejected:  { label: '已拒绝', color: 'var(--c-danger)' },
                completed: { label: '已到账', color: 'var(--c-success)' },
              };
              const s = statusMap[item.status] || { label: item.status, color: 'var(--c-text-tertiary)' };
              return (
                <div key={item.id} style={{ border: '1px solid var(--c-border)', borderRadius: 12, padding: '14px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>{item.user_name || item.user_phone}</div>
                      <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 2 }}>{item.created_at?.slice(0, 16)}</div>
                    </div>
                    <span style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.label}</span>
                  </div>
                  <div style={{ fontSize: 14, marginBottom: 4 }}>¥{item.amount_cny} → {item.amount_usdt} USDT</div>
                  <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', wordBreak: 'break-all', marginBottom: 10 }}>{item.trc20_address}</div>
                  {item.reject_reason && (
                    <div style={{ fontSize: 12, color: 'var(--c-danger)', marginBottom: 8 }}>拒绝原因：{item.reject_reason}</div>
                  )}
                  {item.status === 'pending' && (
                    wdRejectId === item.id ? (
                      <div>
                        <input className="input" placeholder="拒绝原因（可选）"
                          value={wdRejectReason} onChange={e => setWdRejectReason(e.target.value)}
                          style={{ marginBottom: 8 }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-outline btn-sm" style={{ flex: 1 }}
                            onClick={() => { setWdRejectId(null); setWdRejectReason(''); }}>取消</button>
                          <button className="btn btn-sm" style={{ flex: 1, background: 'var(--c-danger)', color: 'white', border: 'none' }}
                            disabled={wdActionLoading}
                            onClick={() => handleWdReview(item.id, 'reject', wdRejectReason)}>
                            确认拒绝
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-outline btn-sm" style={{ flex: 1, color: 'var(--c-danger)', borderColor: 'var(--c-danger)' }}
                          onClick={() => { setWdRejectId(item.id); setWdRejectReason(''); }}>拒绝</button>
                        <button className="btn btn-primary btn-sm" style={{ flex: 1 }}
                          disabled={wdActionLoading}
                          onClick={() => handleWdReview(item.id, 'approve')}>通过</button>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 清空确认弹窗（需手动输入） */}
      <Modal show={showNukeModal} onClose={() => !nuking && setShowNukeModal(false)} title="清空系统数据">
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <AlertTriangle size={52} color="var(--c-danger)" style={{ margin: '0 auto 14px', display: 'block' }} />
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: 'var(--c-danger)' }}>
            此操作将清空系统所有业务数据！
          </div>
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.8, marginBottom: 16,
            background: 'var(--c-bg)', borderRadius: 10, padding: '12px 14px', textAlign: 'left' }}>
            {nukePreviewErr && <div style={{ color: 'var(--c-danger)' }}>{nukePreviewErr}</div>}
            {!nukePreview && !nukePreviewErr && <div>正在读取清空范围…</div>}
            {nukePreview && (<>
              <div>🗑 除下方“保留”外的<strong>全部业务数据</strong>（含以后新增的业务）：{nukePreview.clearTableCount} 类数据，共 {nukePreview.totalRows} 条</div>
              <div>🗑 上传的图片/视频/导出文件等：{nukePreview.diskEntries} 项，约 {fmtBytes(nukePreview.diskBytes)}</div>
              <div>🗑 账号里的可提现余额、证券主图占用归零（账实保持一致）</div>
              <div style={{ marginTop: 6, color: 'var(--c-success)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={13} strokeWidth={2} /> 保留</div>
              {nukePreview.keep.map(k => (
                <div key={k.table} style={{ paddingLeft: 17 }}>✓ {k.reason}{k.rows ? `（${k.rows} 条）` : ''}</div>
              ))}
              {nukePreview.keepUploads.includes('tutorial') && <div style={{ paddingLeft: 17 }}>✓ 教程视频/图片</div>}
              <div style={{ paddingLeft: 17 }}>✓ 近 {nukePreview.retainRecentHours} 小时内的派发记录与批次（OCR 比对要用）</div>
              <div style={{ marginTop: 6, fontSize: 12 }}>清空前会自动备份数据库；备份失败则不会清空。</div>
            </>)}
          </div>
          <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 8 }}>
            请在下方输入 <strong style={{ color: 'var(--c-danger)' }}>清空全部数据</strong> 以确认：
          </p>
          <input
            className="input"
            placeholder="清空全部数据"
            value={nukeInput}
            onChange={e => setNukeInput(e.target.value)}
            style={{ marginBottom: 16, textAlign: 'center', fontSize: 15 }}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-outline" style={{ flex: 1, height: 46 }}
              onClick={() => setShowNukeModal(false)} disabled={nuking}>取消</button>
            <button className="btn btn-danger" style={{ flex: 1, height: 46 }}
              onClick={handleNuke}
              disabled={nuking || !nukePreview || nukeInput !== NUKE_CONFIRM}>
              {nuking ? '清空中...' : '确认清空'}
            </button>
          </div>
        </div>
      </Modal>

      <TabBar mode="admin" />
    </div>
  );
}
